import { useCallback } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { useIntl } from 'react-intl'

import type { OrchestratedAuctionListingDetails } from '@b-stock/fe-core/bstock-next/queries'
import { ddLogger, showSnackbar } from '@b-stock/fe-core/bstock-react'
import type { AutoPayPaymentMethodPaymentTypeEnum } from '@b-stock/payments-methods-api-client'

import upsertAutoPayMethod from '@/buyer-portal/mutations/upsertAutoPayMethod'
import savedAutoPayQuery from '@/buyer-portal/queries/savedAutoPayQuery'
import type { SavedAutoPayData } from '@/buyer-portal/queries/savedAutoPayQuery'
import { useTrackToastView } from '@/helpers/telemetry/useTrackToastView'

import { getAutoPayTarget } from '../utils'

export const useSaveAutoPayMethod = () => {
  const { formatMessage } = useIntl()
  const queryClient = useQueryClient()
  const trackToastView = useTrackToastView()

  const saveAutoPayMethod = useCallback(
    async ({
      accessToken,
      accountId,
      isWireSelected,
      listing,
      paymentMethodId,
    }: {
      accessToken: string
      accountId: string | null
      isWireSelected: boolean
      listing: OrchestratedAuctionListingDetails
      paymentMethodId: string | undefined
    }): Promise<void> => {
      if (!accountId) return
      const autoPayTarget = getAutoPayTarget(listing)
      if (!autoPayTarget) {
        ddLogger.error(
          `Failed to associate auto-payment method for contract listing ${listing.prettyId}: missing contract id`,
          { accountId, listingId: listing._id }
        )
        return
      }
      const { payForId, payForType } = autoPayTarget
      const paymentType: AutoPayPaymentMethodPaymentTypeEnum = isWireSelected
        ? 'wire'
        : 'digital'

      try {
        const newData: SavedAutoPayData = { paymentMethodId, paymentType }
        await upsertAutoPayMethod(accessToken, {
          ...newData,
          payForId,
          payForType,
        })
        queryClient.setQueryData(
          savedAutoPayQuery.getKey(accessToken, [
            accountId,
            payForId,
            payForType,
          ]),
          newData
        )
      } catch (error) {
        ddLogger.error(
          `Failed to associate auto-payment method for listing ${listing.prettyId}`,
          {
            accountId,
            listingId: listing._id,
            paymentMethodId,
          },
          error as Error
        )
        const toastMessage = formatMessage({
          id: 'BidConfirmationModal.toast.upsertAutoPayError',
        })
        showSnackbar({
          content: toastMessage,
          options: { autoClose: true, type: 'error' },
        })
        trackToastView('Failure', toastMessage)
      }
    },
    [formatMessage, queryClient, trackToastView]
  )

  return { saveAutoPayMethod }
}
